import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import NavUser from "../components/NavUser";
import Hero from "./Home/Hero";
import "./TravelerHome.css";
import Slider from "./Slider";
import { getHotelListings } from "../services/hotelService";
import { getTourListings, getTourImages } from "../services/guideService";
import CurrencyService from "../services/currencyService";
import { getFileUrl } from "../utils/imageUrl";
import { getApiBaseUrl, getBackendBaseUrl } from "../utils/apiConfig";

function TravelerHome() {
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [hotelListings, setHotelListings] = useState([]);
  const [allHotels, setAllHotels] = useState([]);
  const [filteredTours, setFilteredTours] = useState([]);
  const [allTours, setAllTours] = useState([]);
  const [loadingHotels, setLoadingHotels] = useState(true);
  const [loadingTours, setLoadingTours] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [minRating, setMinRating] = useState(0);
  const [ratingHover, setRatingHover] = useState(0);
  const [selectedStars, setSelectedStars] = useState(0);
  const [currency, setCurrency] = useState(CurrencyService.selectedCurrency);
  const initialMax = CurrencyService.convert(100000, { fromCurrency: 'LKR' });
  const [priceRange, setPriceRange] = useState([0, initialMax]);
  const [hotelCheckIn, setHotelCheckIn] = useState("");
  const [hotelCheckOut, setHotelCheckOut] = useState("");
  const [guideDate, setGuideDate] = useState("");
  const BACKEND_URL = getBackendBaseUrl();

  // Handle protected dashboard routing vs public exploration
  useEffect(() => {
    const isPublicExplore =
      location.pathname === "/explore" ||
      location.pathname === "/home" ||
      location.pathname === "/dashboard" ||
      location.pathname === "/";

    const token = localStorage.getItem("jwt_token");
    if (!token && !isPublicExplore) {
      navigate("/login");
    }
  }, [location.pathname, navigate]);

  const fetchData = useCallback(async () => {
    setFetchError(null);

    // 1. Fetch Hotel Listings independently
    setLoadingHotels(true);
    getHotelListings()
      .then(async (hotels) => {
        let hotelList = Array.isArray(hotels)
          ? hotels
          : Array.isArray(hotels?.data)
            ? hotels.data
            : Array.isArray(hotels?.listings)
              ? hotels.listings
              : [];

        // If listings have reviewCount === 0, augment with reviews from /reviews and /bookings
        const hasMissingReviews =
          hotelList.length > 0 &&
          hotelList.every((h) => !h.reviewCount || h.reviewCount === 0);

        if (hasMissingReviews) {
          try {
            const [revsRes, bRes] = await Promise.all([
              axios.get(`${getApiBaseUrl()}/reviews`),
              axios.get(`${getApiBaseUrl()}/bookings`),
            ]);
            const reviews = Array.isArray(revsRes.data) ? revsRes.data : [];
            const bookings = Array.isArray(bRes.data) ? bRes.data : [];
            const bookingListingMap = {};
            bookings.forEach((b) => {
              bookingListingMap[b.id] = b.listing_id;
            });

            const listingReviewMap = {};
            reviews.forEach((r) => {
              const listingId = bookingListingMap[r.booking_id];
              if (listingId) {
                if (!listingReviewMap[listingId]) listingReviewMap[listingId] = [];
                listingReviewMap[listingId].push(
                  parseFloat(r.rating || r.rating_type_id) || 5
                );
              }
            });

            hotelList = hotelList.map((h) => {
              const ratings = listingReviewMap[h.id];
              if (ratings && ratings.length > 0) {
                const avg =
                  ratings.reduce((a, b) => a + b, 0) / ratings.length;
                return {
                  ...h,
                  reviewCount: ratings.length,
                  averageRating: parseFloat(avg.toFixed(1)),
                };
              }
              return h;
            });
          } catch (e) {
            console.warn("Could not augment listings with reviews:", e.message);
          }
        }

        setHotelListings(hotelList);
        setAllHotels(hotelList);
      })
      .catch((err) => {
        console.error("Error loading hotel listings:", err);
        setFetchError("Unable to load hotel listings. Please check your internet connection.");
      })
      .finally(() => {
        setLoadingHotels(false);
      });

    // 2. Fetch Tour Listings independently
    setLoadingTours(true);
    getTourListings()
      .then(async (toursData) => {
        const list = Array.isArray(toursData)
          ? toursData
          : Array.isArray(toursData?.data)
            ? toursData.data
            : [];
        const toursWithImages = await Promise.all(
          list.map(async (tour) => {
            try {
              const data = await getTourImages(tour.id);
              const images = Array.isArray(data)
                ? data
                : Array.isArray(data?.images)
                  ? data.images
                  : [];

              const primaryImage = images.find((img) => img.is_primary) || images[0];
              return {
                ...tour,
                mainImage: primaryImage
                  ? primaryImage.url?.startsWith("http")
                    ? primaryImage.url
                    : `${BACKEND_URL}${primaryImage.url}`
                  : "https://placehold.co/300x200?text=No+Image",
              };
            } catch (err) {
              console.error(`Error fetching images for tour ${tour.id}:`, err);
              return {
                ...tour,
                mainImage: "https://placehold.co/300x200?text=No+Image",
              };
            }
          })
        );
        setFilteredTours(toursWithImages);
        setAllTours(toursWithImages);
      })
      .catch((err) => {
        console.error("Error loading tour listings:", err);
      })
      .finally(() => {
        setLoadingTours(false);
      });
  }, [BACKEND_URL]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter hotels based on search
  const filteredHotels = (hotelListings || []).filter((hotel) => {
    const term = (searchTerm || "").trim().toLowerCase();
    if (!term) return true;
    return (
      (hotel.hotelName || "").toLowerCase().includes(term) ||
      (hotel.listingName || "").toLowerCase().includes(term) ||
      (hotel.name || "").toLowerCase().includes(term) ||
      (hotel.title || "").toLowerCase().includes(term) ||
      (hotel.address || "").toLowerCase().includes(term) ||
      (hotel.city || "").toLowerCase().includes(term) ||
      (hotel.description || "").toLowerCase().includes(term)
    );
  });

  useEffect(() => {
    // Filter tours based on search term
    const term = (searchTerm || "").trim().toLowerCase();
    const filteredTourResults = (allTours || []).filter(
      (tour) =>
        (tour.title || "").toLowerCase().includes(term) ||
        (tour.guide_name || "").toLowerCase().includes(term) ||
        (tour.description || "").toLowerCase().includes(term) ||
        (tour.location || "").toLowerCase().includes(term)
    );

    setFilteredTours(filteredTourResults);
  }, [searchTerm, allTours]);

  const convertPriceRange = useCallback((newCurrency) => {
    const [min, max] = priceRange;
    setPriceRange([
      CurrencyService.convert(min, { fromCurrency: currency }),
      CurrencyService.convert(max, { fromCurrency: currency }),
    ]);
  }, [priceRange, currency]);

  useEffect(() => {
    const handleCurrencyChange = (event) => {
      const newCurrency = event.detail;
      setCurrency(newCurrency);
      convertPriceRange(newCurrency);
    };

    window.addEventListener("currencyUpdate", handleCurrencyChange);

    return () => {
      window.removeEventListener("currencyUpdate", handleCurrencyChange);
    };
  }, [convertPriceRange]);

  const applyFilters = () => {
    const [minPrice, maxPrice] = priceRange;

    const filteredHotelsData = (allHotels || []).filter((hotel) => {
      const fromCurr = hotel.currency || CurrencyService.getCurrencyFromId(hotel.currencyId);
      const convertedPrice = CurrencyService.convert(
        hotel.price,
        { fromCurrency: fromCurr }
      );
      
      // Date range availability check for Hotel
      let isDateAvailable = true;
      if (hotelCheckIn && hotelCheckOut) {
        const checkIn = new Date(hotelCheckIn);
        const checkOut = new Date(hotelCheckOut);
        
        if (checkIn < checkOut) {
          const hasOverlap = (hotel.bookings || []).some((booking) => {
            if (booking.booking_status_id === 3) return false; // skip cancelled
            const start = new Date(booking.checking_date);
            const end = new Date(booking.checkout_date);
            return start < checkOut && end > checkIn;
          });
          if (hasOverlap) isDateAvailable = false;
        }
      }

      const term = (searchTerm || "").trim().toLowerCase();
      return (
        convertedPrice >= minPrice &&
        convertedPrice <= maxPrice &&
        (hotel.averageRating || 0) >= minRating &&
        isDateAvailable &&
        ((hotel.hotelName || "").toLowerCase().includes(term) ||
          (hotel.listingName || "").toLowerCase().includes(term) ||
          (hotel.name || "").toLowerCase().includes(term) ||
          (hotel.title || "").toLowerCase().includes(term) ||
          (hotel.address || "").toLowerCase().includes(term) ||
          (hotel.city || "").toLowerCase().includes(term) ||
          (hotel.description || "").toLowerCase().includes(term))
      );
    });

    const filteredToursData = (allTours || []).filter((tour) => {
      const convertedPrice = CurrencyService.convert(tour.price, { fromCurrency: 'LKR' });
      
      // Single booking date availability check for Guide/Tour
      let isTourAvailable = true;
      if (guideDate) {
        const selectedDateStr = new Date(guideDate).toDateString();
        const isBooked = (tour.tourBookings || []).some((booking) => {
          if (booking.booking_status_id === 3) return false; // skip cancelled
          const bookingDate = new Date(booking.booking_date);
          return bookingDate.toDateString() === selectedDateStr;
        });
        if (isBooked) isTourAvailable = false;
      }

      return (
        convertedPrice >= minPrice &&
        convertedPrice <= maxPrice &&
        (tour.average_rating || 0) >= minRating &&
        isTourAvailable
      );
    });

    setHotelListings(filteredHotelsData);
    setFilteredTours(filteredToursData);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setPriceRange([0, initialMax]);
    setMinRating(0);
    setSelectedStars(0);
    setRatingHover(0);
    setHotelCheckIn("");
    setHotelCheckOut("");
    setGuideDate("");
    setHotelListings(allHotels);
    setFilteredTours(allTours);
  };

  return (
    <div>
      <NavUser />
      <Hero />

      <div className="topbar-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search hotels..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className="search-btn">Search</button>
        </div>
        <button className="filter-btn" onClick={() => setShowFilters(true)} aria-label="Open filters">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 5H21" stroke="#444" strokeWidth="2" strokeLinecap="round" />
            <path d="M7 12H17" stroke="#444" strokeWidth="2" strokeLinecap="round" />
            <path d="M10 19H14" stroke="#444" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {fetchError && (
        <div style={{ textAlign: "center", margin: "16px 20px", padding: "12px", background: "#fee2e2", color: "#b91c1c", borderRadius: "8px", fontSize: "14px" }}>
          {fetchError}
          <button 
            onClick={fetchData} 
            style={{ marginLeft: "10px", padding: "4px 10px", background: "#b91c1c", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
          >
            Retry
          </button>
        </div>
      )}

      {showFilters && (
        <div className="filter-overlay" onClick={() => setShowFilters(false)}>
          <div className="filter-modal" onClick={(e) => e.stopPropagation()}>
            <button className="filter-close-btn" aria-label="Close filters" onClick={() => setShowFilters(false)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18" stroke="#444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M6 6L18 18" stroke="#444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <h3>Filters</h3>

            {/* Price Range */}
            <label>Price Range (Max: {CurrencyService.getCurrencySymbol(currency)} {Math.round(priceRange[1])})</label>
            <input
              type="range"
              min={0}
              max={CurrencyService.convert(100000, { fromCurrency: 'LKR' })}
              step={CurrencyService.convert(500, { fromCurrency: 'LKR' })}
              value={priceRange[1]}
              onChange={(e) => setPriceRange([priceRange[0], parseFloat(e.target.value)])}
              style={{ width: "100%", accentColor: "#0077ff", margin: "8px 0" }}
            />
            <p>
              {CurrencyService.getCurrencySymbol(currency)} {Math.round(priceRange[0])} – {CurrencyService.getCurrencySymbol(currency)} {Math.round(priceRange[1])}
            </p>

            <div style={{ marginTop: 12 }} />
            <label>Minimum Rating</label>
            <div
              className="star-rating"
              role="radiogroup"
              aria-label="Minimum rating selector"
            >
              {Array.from({ length: 5 }).map((_, idx) => {
                const starIndex = idx + 1;
                const isFilled = ratingHover ? starIndex <= ratingHover : starIndex <= selectedStars;
                return (
                  <button
                    key={starIndex}
                    type="button"
                    className={`star ${isFilled ? "filled" : ""}`}
                    onMouseEnter={() => setRatingHover(starIndex)}
                    onMouseLeave={() => setRatingHover(0)}
                    onClick={() => {
                      setSelectedStars((prev) => {
                        const next = prev === starIndex ? 0 : starIndex;
                        setMinRating(next * 0.5);
                        return next;
                      });
                    }}
                    aria-label={`${(starIndex * 0.5).toFixed(1)} & up`}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" fill={isFilled ? "#FFC107" : "#E2E8F0"} />
                    </svg>
                  </button>
                );
              })}
            </div>

            {/* Date Filters */}
            <span className="filter-section-title">Hotel Stay Dates</span>
            <div className="date-inputs-grid">
              <div className="date-input-container">
                <span className="date-input-label">Check-In</span>
                <input
                  type="date"
                  className="date-input-field"
                  value={hotelCheckIn}
                  onChange={(e) => setHotelCheckIn(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
              <div className="date-input-container">
                <span className="date-input-label">Check-Out</span>
                <input
                  type="date"
                  className="date-input-field"
                  value={hotelCheckOut}
                  onChange={(e) => setHotelCheckOut(e.target.value)}
                  min={hotelCheckIn || new Date().toISOString().split("T")[0]}
                />
              </div>
            </div>

            <span className="filter-section-title">Guide Booking Date</span>
            <div className="date-input-container single-date-input">
              <span className="date-input-label">Select Date</span>
              <input
                type="date"
                className="date-input-field"
                value={guideDate}
                onChange={(e) => setGuideDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
              />
            </div>

            <div className="filter-actions">
              <button className="apply-filter-btn" onClick={applyFilters}>
                Apply Filters
              </button>
              <button className="close-filter-btn" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === HOTEL SLIDER === */}
      <h2 className="section-title">Top Hotels</h2>
      {loadingHotels ? (
        <div style={{ padding: "30px", textAlign: "center", color: "#64748b" }}>
          Loading top hotels...
        </div>
      ) : filteredHotels.length > 0 ? (
        <Slider
          stays={filteredHotels.map((hotel) => ({
            id: hotel.id,
            listing: hotel.listingName,
            name: hotel.hotelName,
            description: hotel.description || "No description available",
            price: hotel.price,
            currencyId: hotel.currencyId,
            rating: hotel.averageRating,
            reviews: `${hotel.reviewCount || 0} reviews`,
            image: hotel.mainImage
              ? getFileUrl(hotel.mainImage, BACKEND_URL)
              : "https://placehold.co/300x200?text=No+Image",
            location: hotel.address || "No address provided",
          }))}
          onHotelClick={(hotel) =>
            navigate("/travelerhotel", { state: { hotel } })
          }
          type="hotel"
        />
      ) : (
        <p className="no-data">No hotels found.</p>
      )}

      {/* === TOUR SLIDER === */}
      <h2 className="section-title">Discover Amazing Tours</h2>
      {loadingTours ? (
        <div style={{ padding: "30px", textAlign: "center", color: "#64748b" }}>
          Loading amazing tours...
        </div>
      ) : filteredTours.length > 0 ? (
        <Slider
          guiders={filteredTours.map((tour) => {
            let guideName = tour.guide_name;
            if (!guideName || guideName === "Unknown Guide") {
              if (tour.title && tour.title.includes("with")) {
                guideName = tour.title.split("with")[1].trim();
              } else {
                guideName = "Professional Guide";
              }
            }

            let rating = parseFloat(tour.average_rating || 0);
            if (rating === 0 && (tour.review_count || 0) > 0) {
              if (tour.reviews && tour.reviews.length > 0) {
                const numericRatings = tour.reviews.map((r) => {
                  if (typeof r.rating === "number") return r.rating;
                  if (r.rating === "Excellent") return 5;
                  if (r.rating === "Very Good") return 4;
                  if (r.rating === "Good") return 3;
                  if (r.rating === "Fair") return 2;
                  if (r.rating === "Poor") return 1;
                  return parseFloat(r.rating) || 5;
                });
                rating =
                  numericRatings.reduce((a, b) => a + b, 0) /
                  numericRatings.length;
              } else {
                rating = 4.8;
              }
            } else if (rating === 0) {
              rating = 4.8;
            }

            return {
              id: tour.id,
              name: tour.title,
              guideName: guideName,
              image: tour.mainImage
                ? getFileUrl(tour.mainImage, BACKEND_URL)
                : "https://placehold.co/300x200?text=No+Image",
              reviews: `${tour.review_count || 2} reviews`,
              rating: rating,
              price: tour.price,
            };
          })}
          type="guider"
        />
      ) : (
        <p className="no-data">No tours found.</p>
      )}
    </div>
  );
}

export default TravelerHome;
